// src/utils/lockboxes.js
// A listing's lockboxes: one Push Button and one Electronic at most, each with an optional note saying where it
// is ("Front Door").
//
// Henry, 2026-09-25: "In the lockbox section on the listing form, they want to be able to add an electronic
// lockbox, a pushbutton lockbox. There should be an Add Lockbox button so that way, if they need to add another
// one, they can." His answers: emails list them all; one of each kind, never two of the same; an optional note
// per box; Settings > Lockbox Inventory adds a box instead of replacing everything, replacing only the same kind;
// open houses already booked pick up a change.
//
// Shape on the deal: lockboxes: [{ type, id, where }]. The old pair (lockboxType, selectedLockbox) stays and
// always mirrors the FIRST entry, so every screen and both Cloud Functions that have not learned the list yet
// still show one real lockbox instead of nothing. A deal that never gained the list reads as a one-entry list
// built from that pair, so nothing has to be converted and no old listing changes until someone edits it.
//
// Pure: no React, no Firestore. Spec: docs/superpowers/specs/2026-09-25-two-lockboxes-design.md.

export const LOCKBOX_TYPES = ["Push Button", "Electronic"];

const str = (v) => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");

// Some old deals stored the whole lockbox object in selectedLockbox instead of its id.
export function lockboxIdOf(value) {
  if (value && typeof value === "object") return str(value.id);
  return str(value);
}

function cleanEntry(raw) {
  const e = raw && typeof raw === "object" ? raw : {};
  const type = LOCKBOX_TYPES.includes(str(e.type)) ? str(e.type) : "";
  const id = lockboxIdOf(e.id ?? e.selectedLockbox);
  if (!type || !id) return null;
  return { type, id, where: str(e.where) };
}

// Every lockbox on this listing, in order, at most one per type. Reads the list when the deal has one, and falls
// back to the old pair for every listing that has not been edited since 09/25/2026. Never throws.
export function dealLockboxes(deal) {
  const d = deal && typeof deal === "object" ? deal : {};
  const list = Array.isArray(d.lockboxes) ? d.lockboxes.map(cleanEntry).filter(Boolean) : [];
  const out = [];
  for (const e of list) if (!out.some((x) => x.type === e.type)) out.push(e);
  if (out.length) return out;
  const legacy = cleanEntry({ type: d.lockboxType, id: d.selectedLockbox });
  return legacy ? [legacy] : [];
}

export function hasLockbox(deal) {
  return dealLockboxes(deal).length > 0;
}

// The one lockbox the not-yet-updated screens and Cloud Functions still read: the first in the list.
export function primaryLockbox(deal) {
  return dealLockboxes(deal)[0] || null;
}

// The fields a save writes for a list of lockboxes: the list itself plus the old pair kept in step with the first
// entry. An empty list clears both, the way Unassign always has.
export function lockboxesPatch(list) {
  const clean = [];
  for (const e of (Array.isArray(list) ? list : []).map(cleanEntry).filter(Boolean)) {
    if (!clean.some((x) => x.type === e.type)) clean.push(e);
  }
  const first = clean[0] || null;
  return {
    lockboxes: clean,
    lockboxType: first ? first.type : "",
    selectedLockbox: first ? first.id : "",
  };
}

// Add or replace one box. A listing holds one of each type (Henry, 2026-09-25), so a second box of the same type
// takes the place of the one already there; the caller frees whatever this returns in `replaced`.
export function withLockbox(deal, entry) {
  const next = cleanEntry(entry);
  const current = dealLockboxes(deal);
  if (!next) return { list: current, replaced: null };
  const replaced = current.find((e) => e.type === next.type && e.id !== next.id) || null;
  const list = current.some((e) => e.type === next.type)
    ? current.map((e) => (e.type === next.type ? next : e))
    : [...current, next];
  return { list, replaced };
}

// Take one box off the listing, by its id.
export function withoutLockbox(deal, id) {
  const gone = lockboxIdOf(id);
  const current = dealLockboxes(deal);
  return {
    list: current.filter((e) => e.id !== gone),
    removed: current.find((e) => e.id === gone) || null,
  };
}

// Which types can still be added: what the Add Lockbox button offers, and "" when the listing holds both.
export function addableTypes(deal) {
  const taken = dealLockboxes(deal).map((e) => e.type);
  return LOCKBOX_TYPES.filter((t) => !taken.includes(t));
}

// The one wording rule for lockboxes in text a person reads, used by the vendor emails, the hosting-agent email
// and the server's host email, so the three can never drift (Henry, 2026-09-25: "list them all").
//   entries:      [{ type, number, code, where }] - the boxes with their inventory details already looked up.
//   withNumber:   the vendor emails name the box ("Push Button #27"); the hosting emails never have.
//   includeCodes: false for an outside host, who never gets a code. An Electronic box NEVER shows its number or
//                 pin, whoever is reading: the agent opens it with their own app credentials.
// Returns one line: "Push Button #27 (code 4521) at the Front Door, Electronic at the Back Gate", or "" when the
// listing has no lockbox at all.
export function accessLines(entries, { withNumber = false, includeCodes = true } = {}) {
  return (Array.isArray(entries) ? entries : [])
    .map((raw) => {
      const e = raw && typeof raw === "object" ? raw : {};
      const type = LOCKBOX_TYPES.includes(str(e.type)) ? str(e.type) : "";
      if (!type) return "";
      const where = str(e.where);
      const tail = where ? ` at the ${where}` : "";
      if (type === "Electronic") return `Electronic${tail}`;
      const number = withNumber && str(e.number) ? ` #${str(e.number)}` : "";
      const code = includeCodes && str(e.code) ? ` (code ${str(e.code)})` : "";
      return `${type}${number}${code}${tail}`;
    })
    .filter(Boolean)
    .join(", ");
}

// True when a lockbox doc (from either inventory collection) belongs to this deal.
export function lockboxBelongsToDeal(lockboxDoc, dealId) {
  const id = str(dealId);
  return Boolean(id) && str(lockboxDoc && lockboxDoc.assignedDealId) === id;
}

// Do two lists say the same thing? Used so a save or a self-heal writes only when something really moved.
export function sameLockboxes(a, b) {
  const x = (Array.isArray(a) ? a : []).map(cleanEntry).filter(Boolean);
  const y = (Array.isArray(b) ? b : []).map(cleanEntry).filter(Boolean);
  if (x.length !== y.length) return false;
  return x.every((e, i) => e.type === y[i].type && e.id === y[i].id && e.where === y[i].where);
}
